// src/services/api.ts
import axios from 'axios';
import { ProductVariant } from '../utils/variantIdForSize';

// 🛑 CRITICAL FIX: The IP address was invalid. 
// Replace 192.168.1.8 with your Mac's actual IP address.
//const API_BASE_URL = 'http://localhost:8005';
const API_BASE_URL = '';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Define a type for the data we are sending
interface ProfileData {
  file: File;
  name: string;      // Add name
  email: string;     // Add email
  mobile: string;    // Add mobile
  height: string;
  weight: string;
  bodyType: string;
  profession: string;
  age: string; // Ensure age is part of the type
  provider?: string; // Optional provider
  occasion?: string;
}

export const uploadSelfieAndAnalyze = async (data: ProfileData) => {
  const formData = new FormData();
  
  formData.append('file', data.file);
  formData.append('name', data.name);       // Add name
  formData.append('email', data.email);     // Add email
  formData.append('mobile', data.mobile);   // Add mobile
  formData.append('height', data.height);
  formData.append('weight', data.weight);
  formData.append('bodyType', data.bodyType);
  formData.append('profession', data.profession);
  // Age was missing from the ProfileData interface, added it for consistency
  formData.append('age', data.age); 
  formData.append('provider', data.provider || 'gemini');

  // console.info('[INFO] Starting analysis API call to:', API_BASE_URL);
  // formData.forEach((value, key) => {
  //   console.info(`[INFO] FormData entry - ${key}: ${value instanceof File ? value.name : value}`);
  // });

  try {
    const response = await api.post('/analyze-face/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    console.info('[INFO] Analysis response received:', response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('[ERROR] Analysis API call failed:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config?.url
      });
    } else {
      console.error('[ERROR] Unexpected error in analysis API:', error);
    }
    throw error;
  }
};

export const getAllProducts = async () => {
  try {
    // console.info('[INFO] Fetching all products from API:', API_BASE_URL + '/products/');
    const response = await api.get('/products/');
    // console.info('[INFO] Products fetched successfully:', response.data.length, 'items');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('[ERROR] Fetch products failed:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data
      });
    } else {
      console.error('[ERROR] Unexpected error in fetch products:', error);
    }
    throw error;
  }
};

export interface RecommendedPantItem {
  id: string;
  name: string;
  type: string;
  color: string;
  price: string;
  regular_price: string;
  /** Primary product image (preferred over legacy `image_urls`). */
  primary_image_url: string;
  /** Legacy multi-image support (kept for backward compatibility). */
  image_urls?: string[];
  url: string;
  size: string;
  variant_id: string;
  /** Map of size -> Shopify variant ID; when set, used at checkout for the selected size */
  variant_ids?: Record<string, string>;
  product_image_link: string;
  outfit_image_url: string;
  hex_code?: string;
}

export interface ComboProductItem {
  id: string;
  name: string;
  price: string;
  regular_price: string;
  /** Legacy single-image field (kept for backward compatibility). */
  primary_image_url?: string;
  /** Gallery of product images; first entry is the primary image. */
  image_urls?: string[];
  product_image_link?: string;
  video_url?: string;
  color: string;
  size: string;
  variant_id: string;
  variant_ids?: Record<string, string>;
  /** Sizes with their Shopify variant IDs, e.g. [{ size: "S - 38", variant_id: "123" }] */
  variants?: ProductVariant[];
  recommended_size?: string;
  recommended_variant_id?: string;
  rating?: number;
  review_count?: number;
  product_tags?: string[];
  /** JSON-encoded map of spec title -> description, e.g. '{ "Breathable build": "..." }' */
  fabric_details?: string;
  hex_code?: string;
  url?: string;
  type: string;
}

export interface ComboShoesItem {
  combo_id: string;
  name: string;
  color?: string;
  hex_code?: string;
  price: string | number;
  regular_price?: string;
  /** Legacy single-image field (kept for backward compatibility). */
  image_url?: string;
  /** Gallery of product images; first entry is the primary image. */
  image_urls?: string[];
  url?: string;
  recommended_size?: string;
  product_image_link?: string;
  rating?: number;
  review_count?: number;
  product_tags?: string[];
  /** JSON-encoded map of spec title -> description, e.g. '{ "Breathable build": "..." }' */
  fabric_details?: string;
  video_url?: string;
}

export interface ComboEntry {
  rank: number;
  shirt: ComboProductItem;
  pant: ComboProductItem;
  shoes: ComboShoesItem;
}

export interface FaceComboResponse {
  is_human_face: boolean;
  hair_color?: string;
  skin_tone?: string;
  eye_color?: string;
  face_type?: string;
  /** New multi-combo format: array of ranked combos */
  combos?: ComboEntry[];
  // Legacy single-combo fields (backward compat)
  shirt?: ComboProductItem;
  pant?: ComboProductItem;
  pants?: ComboProductItem;
  top?: ComboProductItem;
  trouser?: ComboProductItem;
  trousers?: ComboProductItem;
  bottom?: ComboProductItem;
  shoes?: ComboShoesItem;
}

export const analyzeFaceCombo = async (data: ProfileData): Promise<FaceComboResponse> => {
  const formData = new FormData();
  formData.append('file', data.file);
  formData.append('name', data.name);
  formData.append('email', data.email);
  formData.append('mobile', data.mobile);
  formData.append('height', data.height);
  formData.append('weight', data.weight);
  formData.append('bodyType', data.bodyType);
  formData.append('profession', data.profession);
  formData.append('age', data.age);
  formData.append('provider', data.provider || 'gemini');
  if (data.occasion) formData.append('occasion', data.occasion);

  try {
    const response = await api.post<FaceComboResponse>('/analyze-face-combo/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    console.info('[INFO] Face combo response received:', response.data);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('[ERROR] analyzeFaceCombo API call failed:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config?.url
      });
    } else {
      console.error('[ERROR] Unexpected error in analyzeFaceCombo:', error);
    }
    throw error;
  }
};

export const getRecommendedPants = async (shirtSlug: string): Promise<RecommendedPantItem[]> => {
  try {
    const response = await api.get<RecommendedPantItem[]>('/recommended-pants/', {
      params: { shirt_slug: shirtSlug },
    });
    return response.data || [];
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('[ERROR] Fetch recommended pants failed:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data
      });
    } else {
      console.error('[ERROR] Unexpected error in fetch recommended pants:', error);
    }
    throw error;
  }
};
